import React, { useMemo } from 'react';
import {
  Treemap,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entryPrices = {};
  const exitPrices = {};

  records.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entryPrices[symbol]) entryPrices[symbol] = [];
      entryPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity
      });
    } else if (record.action === 'exit') {
      if (!exitPrices[symbol]) exitPrices[symbol] = [];
      exitPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity
      });
    }
  });

  const pairs = [];
  Object.keys(entryPrices).forEach(symbol => {
    const entries = [...entryPrices[symbol]];
    const exits = exitPrices[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty
        });
      }
    });
  });

  return pairs;
};

// Custom content renderer for Treemap
const CustomContent = (props) => {
  const { x, y, width, height, name, profit, trades, wins, losses } = props;

  if (width < 30 || height < 30) {
    return null; // Don't render tiny cells
  }

  const isProfit = profit >= 0;
  const bgColor = isProfit ? '#22c55e' : '#ef4444';
  const textColor = '#ffffff';

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={bgColor}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
        ry={4}
      />
      {width > 50 && height > 40 && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 8}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(18, width / 4)}
            fontWeight="bold"
          >
            {name}
          </text>
          <text
            x={x + width / 2}
            y={y + height / 2 + 12}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(14, width / 5)}
          >
            {isProfit ? '+' : ''}${profit?.toFixed(0)}
          </text>
        </>
      )}
      {width > 50 && height > 60 && trades && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 28}
          textAnchor="middle"
          fill={textColor}
          fontSize={Math.min(11, width / 6)}
          opacity={0.9}
        >
          {trades} trades
        </text>
      )}
    </g>
  );
};

/**
 * P&L Treemap Chart
 * Block size = P&L amount
 * Color = Green (profit) / Red (loss)
 */
const PLTreemap = ({ records, pairs = [] }) => {
  // Calculate P&L by symbol
  const { treeData, stockData } = useMemo(() => {
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    if (pairsToUse.length === 0) {
      return { treeData: [], stockData: {} };
    }

    const symbolPL = {};
    pairsToUse.forEach(pair => {
      const symbol = pair.symbol;
      if (!symbolPL[symbol]) {
        symbolPL[symbol] = { profit: 0, trades: 0, wins: 0, losses: 0 };
      }
      symbolPL[symbol].profit += pair.profit || 0;
      symbolPL[symbol].trades += 1;
      if ((pair.profit || 0) > 0) {
        symbolPL[symbol].wins += 1;
      } else {
        symbolPL[symbol].losses += 1;
      }
    });

    // Convert to treemap format
    const treeData = Object.entries(symbolPL).map(([symbol, data]) => ({
      name: symbol,
      size: Math.abs(data.profit),
      profit: data.profit,
      trades: data.trades,
      wins: data.wins,
      losses: data.losses
    }));

    // Sort by absolute profit descending
    treeData.sort((a, b) => b.size - a.size);

    return { treeData, stockData: symbolPL };
  }, [records, pairs]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isProfit = data.profit >= 0;

      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '150px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '16px' }}>
            {data.name}
          </div>
          <div style={{ fontSize: '13px' }}>
            <div style={{
              color: isProfit ? '#22c55e' : '#ef4444',
              fontWeight: 'bold',
              fontSize: '18px',
              marginBottom: '8px'
            }}>
              P&L: {isProfit ? '+' : ''}${data.profit.toFixed(2)}
            </div>
            <div style={{ color: '#666', marginBottom: '4px' }}>
              Trades: {data.trades} ({data.wins}W / {data.losses}L)
            </div>
            <div style={{ color: '#666' }}>
              Win Rate: {data.trades > 0 ? ((data.wins / data.trades) * 100).toFixed(0) : 0}%
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (treeData.length === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Treemap requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Calculate totals
  const totalProfit = Object.values(stockData).reduce((sum, s) => sum + (s.profit > 0 ? s.profit : 0), 0);
  const totalLoss = Object.values(stockData).reduce((sum, s) => sum + (s.profit < 0 ? Math.abs(s.profit) : 0), 0);
  const netPL = totalProfit - totalLoss;

  return (
    <div style={{ width: '100%' }}>
      {/* Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Stocks</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{treeData.length}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Profit</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#22c55e' }}>+${totalProfit.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Loss</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>-${totalLoss.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Net P&L</div>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: netPL >= 0 ? '#22c55e' : '#ef4444'
          }}>
            {netPL >= 0 ? '+' : ''}${netPL.toFixed(0)}
          </div>
        </div>
      </div>

      {/* Treemap */}
      <div style={{ height: 400, width: '100%' }}>
        <ResponsiveContainer>
          <Treemap
            data={treeData}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="#fff"
            content={<CustomContent />}
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#666'
      }}>
        <span><span style={{ color: '#22c55e', fontWeight: 'bold' }}>Green</span> = Profit</span>
        <span><span style={{ color: '#ef4444', fontWeight: 'bold' }}>Red</span> = Loss</span>
        <span>Larger block = Higher P&L amount</span>
      </div>
    </div>
  );
};

export default PLTreemap;
